import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check, Search } from 'lucide-react';
import { useGlobalFontSize } from '../../../context/GlobalFontSizeContext';
import { MAP_CONTROL_TRIGGER_CLASS } from '../map/MapSelect';
import { FILTER_CHEVRON_SIZE, FILTER_VALUE_TEXT_CLASS, filterTriggerClass } from '../filterControlStyles';
import { floatingSurfaceClass } from '../floatingSurfaceStyles';
import { checklistActionState } from '../../../utils/countryFilters';
import { HAZARD_COLORS } from '../map/hazardMarkup';
import HazardGlyph from '../map/HazardGlyph';

/**
 * Weather layer definitions for OpenWeatherMap
 * Maps 1.0 (free): current weather only
 * Maps 2.0 (paid): 10-day forecast with time parameter
 */
export const WEATHER_LAYERS = [
  { id: 'temp', label: 'Temperature', layerCode: 'temp_new', layerCodePaid: 'TA2' },
  { id: 'precip', label: 'Precipitation', layerCode: 'precipitation_new', layerCodePaid: 'PA0' },
  { id: 'clouds', label: 'Clouds', layerCode: 'clouds_new', layerCodePaid: 'CL' },
  { id: 'wind', label: 'Wind', layerCode: 'wind_new', layerCodePaid: 'WND' },
  { id: 'pressure', label: 'Pressure', layerCode: 'pressure_new', layerCodePaid: 'APM' },
];

/**
 * =============================================================================
 * WEATHER MAP COLOR PALETTES - OpenWeather Maps 2.0
 * =============================================================================
 *
 * HOW TO MODIFY COLORS:
 * ---------------------
 * Format: '{value}:{HEX_COLOR};{value}:{HEX_COLOR};...'
 * - value = threshold in layer units (mm for precip, °C for temp, % for clouds)
 * - HEX_COLOR = 6-char hex (RRGGBB) or 8-char with alpha (RRGGBBAA)
 * - 00 alpha = transparent, FF = fully opaque
 *
 * EXAMPLES:
 * - '0:FF0000' = red at value 0
 * - '10:00FF0080' = green at value 10 with 50% opacity
 *
 * Set to null to use OpenWeather's default palette for that layer.
 *
 * USEFUL HEX COLORS:
 * - Light Blue: 87CEEB (SkyBlue), 00BFFF (DeepSkyBlue), 1E90FF (DodgerBlue)
 * - Dark Blue: 4169E1 (RoyalBlue), 0000CD (MediumBlue), 00008B (DarkBlue)
 * - Yellow/Orange: FFFF00 (Yellow), FFA500 (Orange), FF4500 (OrangeRed)
 * - Gray: D3D3D3 (LightGray), A9A9A9 (DarkGray), 808080 (Gray)
 * =============================================================================
 */
export const WEATHER_PALETTES = {
  // -------------------------------------------------------------------------
  // DARK MODE - Colors optimized for dark CartoDB base map (#1d1d1d)
  // -------------------------------------------------------------------------
  dark: {
    /**
     * PRECIPITATION (layer code: PA0)
     * Light blue gradient - visible against dark background
     * Values in mm: 0=none, 0.1=light, 1=moderate, 10=heavy, 20=extreme
     */
    precip: '0:00000000;0.1:87CEFA;0.5:87CEEB;1:00BFFF;2:1E90FF;5:4169E1;10:0000FF;20:00008B',

    /**
     * TEMPERATURE (layer code: TA2)
     * Vibrant gradient: purple(cold) → blue → cyan → green → yellow → orange → red(hot)
     * Values in °C: -40=extreme cold, 0=freezing, 20=warm, 40=hot
     */
    temp: '-40:9400D3;-20:4169E1;0:00FFFF;10:00FF00;20:FFFF00;30:FFA500;40:FF4500;50:FF0000',

    /**
     * CLOUDS (layer code: CL)
     * Yellow/orange gradient - standard white clouds invisible on dark background
     * Values in %: 0=clear, 25=partly cloudy, 50=cloudy, 100=overcast
     * Note: Alpha values (last 2 hex chars) control transparency
     */
    clouds: '0:00000000;10:FFD70050;25:FFA50080;50:FF8C00B0;75:FF6347D0;100:FF4500FF',

    wind: null,     // Use OpenWeather default
    pressure: null, // Use OpenWeather default
  },

  // -------------------------------------------------------------------------
  // LIGHT MODE - Colors optimized for light CartoDB base map (#f3f4f6)
  // -------------------------------------------------------------------------
  light: {
    /**
     * PRECIPITATION (layer code: PA0)
     * Deeper blues - visible against white/light background
     */
    precip: '0:00000000;0.1:B0E0E6;0.5:87CEEB;1:4682B4;2:4169E1;5:0000CD;10:00008B;20:191970',

    /**
     * TEMPERATURE (layer code: TA2)
     * Same vibrant gradient as dark mode - works well on both backgrounds
     */
    temp: '-40:9400D3;-20:4169E1;0:00FFFF;10:00FF00;20:FFFF00;30:FFA500;40:FF4500;50:FF0000',

    /**
     * CLOUDS (layer code: CL)
     * Gray/slate gradient - standard white clouds invisible on white background
     */
    clouds: '0:00000000;10:D3D3D350;25:A9A9A980;50:808080B0;75:696969D0;100:2F4F4FFF',

    wind: null,     // Use OpenWeather default
    pressure: null, // Use OpenWeather default
  },
};

/**
 * =============================================================================
 * WEATHER LEGEND DATA - Color scales shown below the map
 * =============================================================================
 *
 * Based on OpenWeatherMap Maps 2.0 official color palettes:
 * https://openweathermap.org/api/weather-map-2
 *
 * Each layer has:
 * - label: Display name
 * - unit: Measurement unit
 * - min/max: Range endpoints for display
 * - stops: Array of {value, color, label?} for gradient and tooltips
 *   - value: threshold value in layer units
 *   - color: hex color (use rgba for transparency)
 *   - label: optional human-readable description
 *
 * Colors match WEATHER_PALETTES above for consistency.
 * =============================================================================
 */
export const WEATHER_LEGEND_DATA = {
  temp: {
    label: 'Temperature',
    unit: '°C',
    min: -40,
    max: 50,
    stops: [
      { value: -40, color: '#9400D3', label: 'Extreme cold' },
      { value: -20, color: '#4169E1', label: 'Very cold' },
      { value: 0, color: '#00FFFF', label: 'Freezing' },
      { value: 10, color: '#00FF00', label: 'Cool' },
      { value: 20, color: '#FFFF00', label: 'Mild' },
      { value: 30, color: '#FFA500', label: 'Warm' },
      { value: 40, color: '#FF4500', label: 'Hot' },
      { value: 50, color: '#FF0000', label: 'Extreme heat' },
    ],
  },
  precip: {
    label: 'Precipitation',
    unit: 'mm',
    min: 0,
    max: 140,
    // Based on OpenWeather PA0 layer: "0:00000000; 0.1:C8969600; 0.2:9696AA00; 0.5:7878BE19; 1:6E6ECD33; 10:5050E1B2; 140:1414FFE5"
    stopsDark: [
      { value: 0, color: 'rgba(0,0,0,0)', label: 'None' },
      { value: 0.1, color: '#87CEFA', label: 'Trace' },
      { value: 0.5, color: '#87CEEB', label: 'Light' },
      { value: 1, color: '#00BFFF', label: 'Light' },
      { value: 2, color: '#1E90FF', label: 'Moderate' },
      { value: 5, color: '#4169E1', label: 'Moderate' },
      { value: 10, color: '#0000FF', label: 'Heavy' },
      { value: 20, color: '#0000CD', label: 'Very heavy' },
      { value: 140, color: '#00008B', label: 'Extreme' },
    ],
    stopsLight: [
      { value: 0, color: 'rgba(0,0,0,0)', label: 'None' },
      { value: 0.1, color: '#B0E0E6', label: 'Trace' },
      { value: 0.5, color: '#87CEEB', label: 'Light' },
      { value: 1, color: '#4682B4', label: 'Light' },
      { value: 2, color: '#4169E1', label: 'Moderate' },
      { value: 5, color: '#0000CD', label: 'Moderate' },
      { value: 10, color: '#00008B', label: 'Heavy' },
      { value: 20, color: '#191970', label: 'Very heavy' },
      { value: 140, color: '#0D0D3B', label: 'Extreme' },
    ],
  },
  clouds: {
    label: 'Cloud Cover',
    unit: '%',
    min: 0,
    max: 100,
    stopsDark: [
      { value: 0, color: 'rgba(0,0,0,0)', label: 'Clear' },
      { value: 10, color: 'rgba(255,215,0,0.3)', label: 'Few clouds' },
      { value: 25, color: '#FFD700', label: 'Partly cloudy' },
      { value: 50, color: '#FFA500', label: 'Cloudy' },
      { value: 75, color: '#FF6347', label: 'Mostly cloudy' },
      { value: 100, color: '#FF4500', label: 'Overcast' },
    ],
    stopsLight: [
      { value: 0, color: 'rgba(0,0,0,0)', label: 'Clear' },
      { value: 10, color: 'rgba(211,211,211,0.3)', label: 'Few clouds' },
      { value: 25, color: '#D3D3D3', label: 'Partly cloudy' },
      { value: 50, color: '#A9A9A9', label: 'Cloudy' },
      { value: 75, color: '#696969', label: 'Mostly cloudy' },
      { value: 100, color: '#2F4F4F', label: 'Overcast' },
    ],
  },
  wind: {
    label: 'Wind Speed',
    unit: 'm/s',
    min: 0,
    max: 50,
    // Based on OpenWeather WND layer: "1:FFFFFF00; 5:EECECC66; 15:B364BCB3; 25:3F213BCC; 50:744CACE6; 100:4600AFFF"
    stops: [
      { value: 0, color: 'rgba(255,255,255,0)', label: 'Calm' },
      { value: 1, color: 'rgba(255,255,255,0.4)', label: 'Light air' },
      { value: 5, color: '#EECECC', label: 'Light breeze' },
      { value: 10, color: '#C9A0DC', label: 'Gentle breeze' },
      { value: 15, color: '#B364BC', label: 'Moderate' },
      { value: 25, color: '#3F213B', label: 'Strong' },
      { value: 50, color: '#744CAC', label: 'Gale force' },
    ],
  },
  pressure: {
    label: 'Pressure',
    unit: 'hPa',
    min: 940,
    max: 1080,
    // Based on OpenWeather APM layer
    stops: [
      { value: 940, color: '#0000CD', label: 'Very low' },
      { value: 980, color: '#4169E1', label: 'Low' },
      { value: 1000, color: '#00CED1', label: 'Normal low' },
      { value: 1013, color: '#00FF00', label: 'Standard' },
      { value: 1025, color: '#FFFF00', label: 'Normal high' },
      { value: 1050, color: '#FFA500', label: 'High' },
      { value: 1080, color: '#FF0000', label: 'Very high' },
    ],
  },
};

/**
 * Weather event types from GDACS
 * Professional circle markers - no emojis
 */
export const EVENT_TYPES = [
  { id: 'cyclone', label: 'Cyclones', color: HAZARD_COLORS.cyclone },
  { id: 'flood', label: 'Floods', color: HAZARD_COLORS.flood },
  { id: 'earthquake', label: 'Earthquakes', color: HAZARD_COLORS.earthquake },
];

/**
 * Production status definitions with colors
 */
export const STATUS_OPTIONS = [
  { id: 'producing', label: 'Producing', color: '#16A34A' },
  { id: 'not_producing', label: 'Not Producing', color: '#6B7280' },
  { id: 'starting_soon', label: 'Starting Soon', color: '#3b82f6' },
  { id: 'ending_soon', label: 'Ending Soon', color: '#f97316' },
];

/**
 * CommoditiesDropdown - Filter by commodities
 */
export const CommoditiesDropdown = ({
  commodities = [],
  regions = [],
  visibleCommodities = [],
  onCommoditiesChange,
}) => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Count regions per commodity
  const regionCounts = {};
  regions.forEach((r) => {
    const key = r.commodityKey;
    regionCounts[key] = (regionCounts[key] || 0) + 1;
  });

  const toggleCommodity = (commodity) => {
    if (visibleCommodities.includes(commodity)) {
      onCommoditiesChange(visibleCommodities.filter((c) => c !== commodity));
    } else {
      onCommoditiesChange([...visibleCommodities, commodity]);
    }
  };

  const allSelected = commodities.length > 0 && visibleCommodities.length === commodities.length;
  const noneSelected = visibleCommodities.length === 0;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-[32px] items-center gap-2 px-3 rounded-md border border-[#C3C4C3] dark:border-[#828382] bg-white dark:bg-[#191919] hover:bg-gray-50 dark:hover:bg-[#232121] transition-colors cursor-pointer"
      >
        <span className="text-[#282A29] dark:text-[#f0f5f2]" style={{ fontSize: fontSize - 2 }}>
          Commodities
        </span>
        <span className="text-[#828382]" style={{ fontSize: fontSize - 3 }}>
          ({visibleCommodities.length})
        </span>
        <ChevronDown
          size={16}
          className={`text-[#828382] transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-[2100] mt-1 min-w-[220px] rounded-lg border border-[#C3C4C3] dark:border-[#828382] bg-white dark:bg-[#191919] shadow-lg overflow-hidden">
          {/* Quick actions */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#E8EBE9] dark:border-[#3a3c3b] bg-[#F0F5F2] dark:bg-[#232121]">
            <button
              type="button"
              onClick={() => onCommoditiesChange([...commodities])}
              disabled={allSelected}
              className="text-xs text-bloom-green hover:underline disabled:opacity-50"
            >
              All
            </button>
            <button
              type="button"
              onClick={() => onCommoditiesChange([])}
              disabled={noneSelected}
              className="text-xs text-[#ef4444] hover:underline disabled:opacity-50"
            >
              None
            </button>
          </div>

          {/* Commodity checkboxes */}
          <div className="py-1 max-h-[250px] overflow-y-auto">
            {commodities.map((commodity) => {
              const isSelected = visibleCommodities.includes(commodity);
              const count = regionCounts[commodity] || 0;
              return (
                <button
                  key={commodity}
                  type="button"
                  onClick={() => toggleCommodity(commodity)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[#F0F5F2] dark:hover:bg-[#232121] ${
                    isSelected ? 'bg-bloom-green/5' : ''
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'bg-bloom-green border-bloom-green'
                        : 'border-[#C3C4C3] dark:border-[#828382]'
                    }`}
                  >
                    {isSelected && <Check size={12} className="text-white" />}
                  </div>
                  <span
                    className="flex-1 text-left text-[#282A29] dark:text-[#f0f5f2]"
                    style={{ fontSize: fontSize - 2 }}
                  >
                    {commodity}
                  </span>
                  <span className="text-[#828382]" style={{ fontSize: fontSize - 3 }}>
                    ({count})
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * SearchableDropdown - Single-select dropdown with search/filter input
 */
const SearchableDropdown = ({
  label,
  options = [],
  value = '',
  onChange,
  allLabel,
  disabled = false,
}) => {
  const { fontSize } = useGlobalFontSize();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setSearchQuery('');
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const selectedLabel = options.find((o) => o.value === value)?.label || allLabel;
  const q = searchQuery.trim().toLowerCase();
  const filteredOptions = q
    ? options.filter((o) => (o.label || o.value || '').toLowerCase().includes(q))
    : options;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen((o) => !o)}
        disabled={disabled}
        className="flex h-[32px] items-center gap-2 px-3 rounded-md border border-[#C3C4C3] dark:border-[#828382] bg-white dark:bg-[#191919] hover:bg-gray-50 dark:hover:bg-[#232121] transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <span className="text-[#282A29] dark:text-[#f0f5f2]" style={{ fontSize: fontSize - 2 }}>
          {label}
        </span>
        <span className="text-[#828382] dark:text-[#a0a5a2] max-w-[120px] truncate" style={{ fontSize: fontSize - 3 }} title={selectedLabel}>
          {selectedLabel}
        </span>
        <ChevronDown
          size={16}
          className={`text-[#828382] dark:text-[#a0a5a2] flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-[2100] mt-1 min-w-[200px] w-max max-w-[280px] max-h-[320px] overflow-hidden rounded-lg border border-[#C3C4C3] dark:border-[#828382] bg-white dark:bg-[#191919] shadow-lg flex flex-col">
          {/* Search bar */}
          <div className="flex-shrink-0 p-2 border-b border-[#E8EBE9] dark:border-[#3a3c3b]">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#828382] dark:text-[#a0a5a2]" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-8 pr-2 py-1.5 rounded border border-[#C3C4C3] dark:border-[#828382] bg-[#F0F5F2] dark:bg-[#232121] text-[#282A29] dark:text-[#f0f5f2] placeholder:text-[#828382] dark:placeholder:text-[#a0a5a2] focus:outline-none focus:ring-1 focus:ring-bloom-green"
                style={{ fontSize: fontSize - 2 }}
              />
            </div>
          </div>
          {/* All option */}
          <div className="flex-shrink-0 py-1">
            <button
              type="button"
              onClick={() => {
                onChange('');
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 text-[#282A29] dark:text-[#f0f5f2] hover:bg-[#F0F5F2] dark:hover:bg-[#232121] ${!value ? 'bg-bloom-green/10 dark:bg-bloom-green/20 text-bloom-green' : ''}`}
              style={{ fontSize: fontSize - 2 }}
            >
              {allLabel}
            </button>
          </div>
          {/* Filtered list */}
          <div className="overflow-y-auto py-1 border-t border-[#E8EBE9] dark:border-[#3a3c3b] min-h-0">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-[#828382] dark:text-[#a0a5a2]" style={{ fontSize: fontSize - 2 }}>
                No matches
              </div>
            ) : (
              filteredOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-1.5 text-[#282A29] dark:text-[#f0f5f2] hover:bg-[#F0F5F2] dark:hover:bg-[#232121] ${value === opt.value ? 'bg-bloom-green/5 dark:bg-bloom-green/15' : ''}`}
                  style={{ fontSize: fontSize - 2 }}
                >
                  {opt.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * useDismissiblePopover - closes an open popover on pointerdown outside
 * `ref.current`, or on Escape. Listeners are only attached while `open` is
 * true so idle dropdowns don't add global handlers.
 */
/**
 * Close-on-outside-pointerdown + Escape. Exported because the map's cluster
 * overview needs the same dismissal, and `pointerdown` (rather than `click`)
 * is load-bearing there: it lands before Leaflet's own marker click, so
 * clicking a second cluster pin closes the first and opens the second instead
 * of the close swallowing the new selection.
 */
export const useDismissiblePopover = (ref, open, setOpen) => {
  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, ref, setOpen]);
};

/**
 * ChecklistDropdown - shared shell for the compact bordered filter dropdowns
 * that show a selected count and open a checklist panel with All/None quick
 * actions. Extracted from StatusDropdown/EventsDropdown, which duplicated
 * this open-state/outside-click/panel/checkbox-row markup exactly; both are
 * now thin wrappers that supply `items`/labels only.
 *
 * `variant`:
 * - 'default' — the h-[32px] row used everywhere on desktop
 *   (GlobalFilterStrip, Home's WeatherMap strip), scaled by the user's global
 *   font size. Border/radius/dark surface match NestedCommoditySelector's
 *   `floating` trigger so Status and Commodity read as one control pair.
 * - 'stacked' — the Analytics filter-bar look (unfilled 32px control, #E2E2E2
 *   border, fixed 12px text, chevron size 14) via `filterTriggerClass`. Used by
 *   the Global Produce Map strip so Status matches the commodity selector
 *   beside it and both match Analytics/Produce Planner.
 * - 'unified' — matches MapSelect's trigger exactly (MAP_CONTROL_TRIGGER_CLASS:
 *   min-h-11, rounded-lg, fixed text-xs, chevron size 14). Used only by
 *   UnifiedMapContainer's mobile "Map controls" panel, where this trigger
 *   sits next to the weather-layer MapSelect and must look identical to it.
 *   Scoped via this prop (rather than changing the default look) so desktop
 *   everywhere else stays pixel-identical.
 *
 * Optional `summary` (node) replaces the `(count)` after the label, and
 * `align="right"` anchors the popup to the trigger's right edge; `allActive`
 * overrides when the All action is disabled; `listClassName` is appended to
 * the checkbox-row list (e.g. a max-height + scroll for long lists);
 * `clearLabel` renames the None action (it stays a red link next to the green
 * All link). An item's `color` draws its indicator dot only — the checkbox is
 * green whatever the item is, because it answers "is this row selected?", not
 * "what status is this?". Colouring the tick by status made those two readings
 * indistinguishable. Items without a `color` get no dot.
 *
 * Optional `searchable` adds a NestedCommoditySelector-style search row between
 * All/None and the checklist, and on open scrolls the list to the first selected
 * row (same center-scroll contract as the commodity selector). Filtering only
 * hides rows; All/None and `selectedIds` stay on the full `items` list. Default
 * off so Status/Events/Mode stay unchanged.
 */
export const ChecklistDropdown = ({
  label,
  count,
  items,
  selectedIds,
  onToggle,
  onSelectAll,
  onSelectNone,
  variant = 'default',
  summary,
  align = 'left',
  allActive,
  listClassName = '',
  clearLabel,
  searchable = false,
  searchPlaceholder = 'Search countries...',
  // `map` variant only: overrides the 40px trigger height (the phone row is 44).
  mapHeightClass = null,
}) => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const listContainerRef = useRef(null);
  const selectedItemRef = useRef(null);

  useDismissiblePopover(containerRef, isOpen, setIsOpen);

  useEffect(() => {
    if (!searchable) return undefined;
    if (isOpen) {
      searchInputRef.current?.focus();
      // Same open-scroll contract as NestedCommoditySelector: wait a tick for
      // the remounted list, then center the first selected row in the scroller.
      const timeoutId = setTimeout(() => {
        const container = listContainerRef.current;
        const item = selectedItemRef.current;
        if (!container || !item) return;
        const target =
          item.offsetTop - container.clientHeight / 2 + item.clientHeight / 2;
        container.scrollTop = Math.max(
          0,
          Math.min(container.scrollHeight - container.clientHeight, target)
        );
      }, 50);
      return () => clearTimeout(timeoutId);
    }
    setSearchQuery('');
    return undefined;
  }, [isOpen, searchable]);

  const { allSelected, noneSelected } = checklistActionState({
    allActive,
    selectedCount: selectedIds.length,
    itemCount: items.length,
  });
  const isUnified = variant === 'unified';
  const isStacked = variant === 'stacked';
  // `map` is the Global Produce Map's floating pill: the selected-count moves
  // ahead of the label as a green badge instead of trailing it as "(4)".
  const isMap = variant === 'map';
  // `pill` is the map's second-tier filter row: a fully rounded 32px chip on the
  // same translucent, blurred surface as the layer rail, so it reads against map
  // tiles. Label + chevron only, per the design.
  const isPill = variant === 'pill';
  const isFixedScale = isUnified || isStacked || isMap || isPill;

  const q = searchable ? searchQuery.trim().toLowerCase() : '';
  const visibleItems = q
    ? items.filter((item) => (item.label || '').toLowerCase().includes(q))
    : items;
  const firstSelectedId = visibleItems.find((item) => selectedIds.includes(item.id))?.id;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className={
          isUnified
            ? `flex items-center gap-2 ${MAP_CONTROL_TRIGGER_CLASS}`
            : isPill
              ? `flex h-[32px] items-center gap-[6px] rounded-[16px] border border-solid px-[11px] transition-colors hover:border-bloom-green ${floatingSurfaceClass(94)} ${isOpen ? 'border-bloom-green' : ''}`
              : isMap
                ? filterTriggerClass({ open: isOpen, tone: 'map', ...(mapHeightClass ? { heightClass: mapHeightClass } : {}) })
                : isStacked
              ? `gap-[8px] ${filterTriggerClass({ open: isOpen })}`
              : 'flex h-[32px] items-center gap-2 px-3 rounded-[8px] border border-[#B1B9B4] bg-white dark:bg-[#282a29] hover:bg-gray-50 dark:hover:bg-[#333534] transition-colors cursor-pointer'
        }
      >
        {isPill ? (
          <span className="flex items-center gap-[6px]">
            {!allActive && count > 0 ? (
              <span className="flex h-[16px] min-w-[16px] shrink-0 items-center justify-center rounded-full bg-[#F0FDF4] px-[4px] text-[10px] font-extrabold text-[#16A34A] dark:bg-[#1C3023] dark:text-[#7EE6A4]">
                {count}
              </span>
            ) : null}
            <span className="whitespace-nowrap font-['Inter'] text-[12px] font-semibold text-[#5D625F] dark:text-[#9AA09A]">
              {label}
            </span>
          </span>
        ) : isMap ? (
          <span className="flex items-center gap-[8px]">
            <span className="flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-[9px] bg-[#F0FDF4] text-[11px] font-extrabold text-[#16A34A] dark:bg-[#1C3023] dark:text-[#7EE6A4]">
              {count}
            </span>
            {/* Matches the commodity selector's value exactly. Both triggers
                share filterTriggerClass's 14px semibold, but the commodity's
                own span overrides it to 12px/#282A29 — so inheriting here left
                Status visibly larger and a different grey beside it. */}
            <span className={`whitespace-nowrap ${FILTER_VALUE_TEXT_CLASS} text-[12px]`}>{label}</span>
          </span>
        ) : (
        /* Baseline-aligned so the smaller count sits on the label's baseline
            instead of being centre-aligned against it. */
        <span
          className={`flex items-baseline gap-1 ${isUnified ? 'text-xs' : ''} ${
            isStacked ? "font-['Inter'] text-[12px]" : ''
          }`}
        >
          <span
            className="text-[#282A29] dark:text-[#f0f5f2]"
            style={isFixedScale ? undefined : { fontSize: fontSize - 2 }}
          >
            {label}
          </span>
          <span
            className="text-[#828382]"
            style={isFixedScale ? undefined : { fontSize: fontSize - 3 }}
          >
            {summary ?? `(${count})`}
          </span>
        </span>
        )}
        <ChevronDown
          size={isPill ? 12 : isMap ? 15 : isFixedScale ? FILTER_CHEVRON_SIZE : 16}
          className={`shrink-0 text-[#828382] transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full z-[2100] mt-1 min-w-[200px] rounded-lg border border-[#C3C4C3] dark:border-[#828382] bg-white dark:bg-[#191919] shadow-lg overflow-hidden`}>
          {/* Quick actions */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#E8EBE9] dark:border-[#3a3c3b] bg-[#F0F5F2] dark:bg-[#232121]">
            <button
              type="button"
              onClick={onSelectAll}
              disabled={allSelected}
              className="font-['Inter'] text-[12px] font-medium leading-[16px] text-bloom-green hover:underline disabled:text-bloom-green disabled:opacity-100 disabled:cursor-default"
            >
              {t('map.filters.all', 'All')}
            </button>
            <button
              type="button"
              onClick={onSelectNone}
              disabled={noneSelected}
              className="font-['Inter'] text-[12px] font-medium leading-[16px] text-[#DC2626] hover:underline disabled:text-[#DC2626] disabled:opacity-100 disabled:cursor-default"
            >
              {clearLabel ?? t('map.filters.none', 'None')}
            </button>
          </div>

          {searchable && (
            <div className="px-2 pt-2 pb-[6px] border-b border-[#E8EBE9] dark:border-[#3a3c3b]">
              <div className="relative">
                <Search size={14} className="absolute left-[10px] top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  className="w-full bg-[#F5F6F5] dark:bg-[#121513] border border-[#E2E2E2] dark:border-[#262B27] rounded-[10px] px-3 py-2 pl-8 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-[#16A34A] dark:focus:border-[#16A34A]"
                  style={{ fontSize: fontSize - 2 }}
                />
              </div>
            </div>
          )}

          {/* Checkbox rows */}
          <div ref={listContainerRef} className={`py-1 ${listClassName}`}>
            {searchable && visibleItems.length === 0 && (
              <div
                className="px-3 py-2 text-[#828382] dark:text-[#a0a5a2]"
                style={{ fontSize: fontSize - 2 }}
              >
                No countries found
              </div>
            )}
            {visibleItems.map((item) => {
              const isSelected = selectedIds.includes(item.id);
              return (
                <button
                  key={item.id}
                  ref={item.id === firstSelectedId ? selectedItemRef : null}
                  type="button"
                  onClick={() => onToggle(item.id)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[#F0F5F2] dark:hover:bg-[#232121] ${
                    isSelected ? 'bg-bloom-green/5' : ''
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'border-transparent'
                        : 'border-[#C3C4C3] dark:border-[#828382]'
                    }`}
                    style={{ backgroundColor: isSelected ? '#16A34A' : 'transparent' }}
                  >
                    {isSelected && <Check size={12} className="text-white" />}
                  </div>
                  {item.glyph ? (
                    <HazardGlyph type={item.glyph} />
                  ) : item.color && (
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                  )}
                  <span
                    className="flex-1 text-left text-[#282A29] dark:text-[#f0f5f2]"
                    style={{ fontSize: fontSize - 2 }}
                  >
                    {item.label}
                  </span>
                  {item.count != null && (
                    <span className="text-[#828382]" style={{ fontSize: fontSize - 3 }}>
                      ({item.count})
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * StatusDropdown - Filter by production status (4 statuses with colors)
 */
export const StatusDropdown = ({
  regions = [],
  visibleStatuses = [],
  onStatusesChange,
  variant = 'default',
  // `map` only: the phone's controls row is 44px where the desktop strip is 40.
  heightClass,
}) => {
  const { t } = useTranslation();

  // Count regions per status
  const statusCounts = {};
  regions.forEach((r) => {
    const status = r.harvest_status;
    statusCounts[status] = (statusCounts[status] || 0) + 1;
  });

  const items = STATUS_OPTIONS.map((status) => ({
    id: status.id,
    label: status.label,
    color: status.color,
    count: statusCounts[status.id] || 0,
  }));

  const toggleStatus = (statusId) => {
    if (visibleStatuses.includes(statusId)) {
      onStatusesChange(visibleStatuses.filter((s) => s !== statusId));
    } else {
      onStatusesChange([...visibleStatuses, statusId]);
    }
  };

  return (
    <ChecklistDropdown
      label={t('map.filters.status', 'Status')}
      count={visibleStatuses.length}
      items={items}
      selectedIds={visibleStatuses}
      onToggle={toggleStatus}
      onSelectAll={() => onStatusesChange(STATUS_OPTIONS.map((s) => s.id))}
      onSelectNone={() => onStatusesChange([])}
      variant={variant}
      mapHeightClass={heightClass}
    />
  );
};

/**
 * LayerToggle - Toggle buttons for weather layers
 * Only one layer can be active at a time
 */
export const LayerToggle = ({ activeLayer, onLayerChange, variant = 'strip' }) => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();

  // `card` renders the layers as one segmented control: a single bordered
  // shell, the active layer a green pill, the rest borderless.
  if (variant === 'card') {
    return (
      <div className="flex flex-wrap items-center gap-[2px] rounded-[10px] border border-[#E8EBE9] bg-white p-[3px] dark:border-[#30363d] dark:bg-[#1d1d1d]">
        {WEATHER_LAYERS.map((layer) => {
          const isActive = activeLayer === layer.id;
          return (
            <button
              key={layer.id}
              type="button"
              onClick={() => onLayerChange(layer.id)}
              className={`font-['Inter'] h-[26px] cursor-pointer whitespace-nowrap rounded-[7px] px-[10px] text-[12px] font-medium transition-colors ${
                isActive
                  ? 'bg-[#DCFCE7] text-[#12823F] dark:bg-[#173322] dark:text-[#7EE6A4]'
                  : 'text-[#5D625F] hover:bg-[#F0F4F1] dark:text-[#828382] dark:hover:bg-[#282a29]'
              }`}
            >
              {t(`home.weatherMap.layers.${layer.id}`, layer.label)}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {WEATHER_LAYERS.map((layer) => {
        const isActive = activeLayer === layer.id;
        return (
          <button
            key={layer.id}
            type="button"
            onClick={() => onLayerChange(layer.id)}
            className={`px-2 py-1 rounded-md border text-xs transition-colors ${
              isActive
                ? 'bg-[#3b82f6] border-[#3b82f6] text-white'
                : 'border-[#C3C4C3] dark:border-[#828382] text-[#828382] hover:bg-gray-50 dark:hover:bg-[#232121]'
            }`}
            style={{ fontSize: fontSize - 3 }}
          >
            {t(`home.weatherMap.layers.${layer.id}`, layer.label)}
          </button>
        );
      })}
    </div>
  );
};

/**
 * EventsToggle - Toggle buttons for weather event types
 * Multiple event types can be active simultaneously
 */
export const EventsToggle = ({ activeTypes = [], onToggle, variant = 'strip' }) => {
  const { fontSize } = useGlobalFontSize();
  const { t } = useTranslation();

  // `card` keeps each event as its own outlined pill (no shared shell) —
  // they toggle independently, unlike the single-choice weather layers.
  if (variant === 'card') {
    return (
      <div className="flex flex-wrap items-center gap-[8px]">
        {EVENT_TYPES.map((event) => {
          const isActive = activeTypes.includes(event.id);
          return (
            <button
              key={event.id}
              type="button"
              onClick={() => onToggle(event.id)}
              className={`font-['Inter'] h-[32px] cursor-pointer whitespace-nowrap rounded-[8px] border px-[12px] text-[12px] font-medium transition-colors ${
                isActive
                  ? 'text-white'
                  : 'border-[#E8EBE9] bg-white text-[#5D625F] hover:border-bloom-green/60 dark:border-[#30363d] dark:bg-[#1d1d1d] dark:text-[#828382]'
              }`}
              style={{
                backgroundColor: isActive ? event.color : undefined,
                borderColor: isActive ? event.color : undefined,
              }}
            >
              {t(`map.events.${event.id}`, event.label)}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {EVENT_TYPES.map((event) => {
        const isActive = activeTypes.includes(event.id);
        return (
          <button
            key={event.id}
            type="button"
            onClick={() => onToggle(event.id)}
            className={`px-2 py-1 rounded-md border text-xs transition-colors ${
              isActive
                ? 'text-white'
                : 'border-[#C3C4C3] dark:border-[#828382] text-[#828382] hover:bg-gray-50 dark:hover:bg-[#232121]'
            }`}
            style={{
              fontSize: fontSize - 3,
              backgroundColor: isActive ? event.color : undefined,
              borderColor: isActive ? event.color : undefined,
            }}
          >
            {t(`map.events.${event.id}`, event.label)}
          </button>
        );
      })}
    </div>
  );
};

/**
 * EventsDropdown - Compact bordered dropdown for weather event types
 * (cyclones/floods/earthquakes). Mirrors the CommoditiesDropdown/StatusDropdown
 * pattern above — a single bordered button showing a selected count that opens
 * a checklist — instead of always-visible chips. Ported from
 * feat/portrait-signage's mobile map-controls layout, where the events row
 * needed to be compact next to the weather-layer MapSelect.
 */
export const EventsDropdown = ({ activeTypes = [], onToggle, variant = 'default', label = null }) => {
  const { t } = useTranslation();

  // Checkbox and title only, per the design review. Neither `color` nor
  // `glyph` is passed, so ChecklistDropdown draws no indicator — the rows it
  // renders for other filters (shipment/production status) still carry theirs,
  // since those colours key a marker on the map and these did not.
  const items = EVENT_TYPES.map((event) => ({
    id: event.id,
    label: t(`map.events.${event.id}`, event.label),
  }));

  const selectAll = () => {
    EVENT_TYPES.forEach((event) => {
      if (!activeTypes.includes(event.id)) onToggle(event.id);
    });
  };
  const selectNone = () => {
    EVENT_TYPES.forEach((event) => {
      if (activeTypes.includes(event.id)) onToggle(event.id);
    });
  };

  return (
    <ChecklistDropdown
      label={label ?? t('map.eventsLabel', 'Events')}
      count={activeTypes.length}
      items={items}
      selectedIds={activeTypes}
      onToggle={onToggle}
      onSelectAll={selectAll}
      onSelectNone={selectNone}
      variant={variant}
    />
  );
};

/**
 * WeatherMapControls - Combined control bar for the weather map
 * Two separate dropdowns: Commodities and Status
 * Responsive: stacks on mobile, wraps on tablet, horizontal on desktop
 */
const WeatherMapControls = ({
  commodities = [],
  regions = [],
  visibleCommodities = [],
  visibleStatuses = [],
  onCommoditiesChange,
  onStatusesChange,
  countryOptions = [],
  selectedCountry = '',
  onCountryChange,
  regionOptions = [],
  selectedRegion = '',
  onRegionChange,
  activeLayer,
  onLayerChange,
  activeEventTypes = [],
  onEventTypeToggle,
  // When true, hide the commodity + status dropdowns (used on /map where
  // GlobalFilterStrip owns those filters). Country, Region, Weather and
  // Events controls remain visible.
  hideFilters = false,
  // `card` renders the row inside the Global Produce Map card: white surface,
  // card-aligned padding and no group dividers. `strip` is the original tinted
  // bar used by the home WeatherMap.
  variant = 'strip',
}) => {
  const { fontSize } = useGlobalFontSize();
  const isCard = variant === 'card';

  return (
    <div
      className={`relative isolate z-[1200] flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3 border-b border-[#E8EBE9] dark:border-[#3a3c3b] ${
        isCard
          ? 'px-[16px] py-[10px] bg-white dark:bg-[#1d1d1d]'
          : 'pr-3 py-2 bg-[#F0F5F2] dark:bg-[#232121]'
      }`}
    >
      {/* Filter dropdowns row */}
      {commodities.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {!hideFilters && (
            <>
              {/* Commodities filter dropdown */}
              <CommoditiesDropdown
                commodities={commodities}
                regions={regions}
                visibleCommodities={visibleCommodities}
                onCommoditiesChange={onCommoditiesChange}
              />

              {/* Status filter dropdown */}
              <StatusDropdown
                regions={regions}
                visibleStatuses={visibleStatuses}
                onStatusesChange={onStatusesChange}
              />
            </>
          )}

          {/* Country dropdown - zoom map (with search) */}
          {countryOptions.length > 0 && (
            <SearchableDropdown
              label="Country"
              options={countryOptions}
              value={selectedCountry}
              onChange={onCountryChange}
              allLabel="All countries"
            />
          )}

          {/* Region dropdown - zoom map within country (with search) */}
          {countryOptions.length > 0 && (
            <SearchableDropdown
              label="Region"
              options={regionOptions}
              value={selectedRegion}
              onChange={onRegionChange}
              allLabel="All regions"
              disabled={!selectedCountry}
            />
          )}

          {/* Divider - hidden on mobile */}
          {!isCard && <div className="hidden sm:block h-6 w-px bg-[#C3C4C3] dark:bg-[#828382]" />}
        </div>
      )}

      {/* Weather and Events row */}
      <div className={`flex flex-wrap items-center ${isCard ? 'gap-[24px]' : 'gap-2 sm:gap-3'}`}>
        {/* Weather layers */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {!isCard && (
            <span className="text-[#828382] hidden xs:inline" style={{ fontSize: fontSize - 3 }}>
              Weather:
            </span>
          )}
          <LayerToggle
            activeLayer={activeLayer}
            onLayerChange={onLayerChange}
            variant={variant}
          />
        </div>

        {/* Divider - hidden on very small screens */}
        {!isCard && <div className="hidden sm:block h-6 w-px bg-[#C3C4C3] dark:bg-[#828382]" />}

        {/* Event toggles */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {!isCard && (
            <span className="text-[#828382] hidden xs:inline" style={{ fontSize: fontSize - 3 }}>
              Events:
            </span>
          )}
          <EventsToggle
            activeTypes={activeEventTypes}
            onToggle={onEventTypeToggle}
            variant={variant}
          />
        </div>
      </div>
    </div>
  );
};

export default WeatherMapControls;
